import React from 'react';
import { AlertTriangle, RefreshCw, WifiOff, Building2, ArrowLeft, Settings } from 'lucide-react';

const SimpleErrorScreen = ({
  message = "Erro na comunicação com o servidor",
  onRetry,
  onGoHome,
  isRetrying = false,
  errorType = "communication" // "communication" ou "namespace"
}) => {
  const isNamespaceError = errorType === "namespace";

  const getIcon = () => {
    if (isNamespaceError) {
      return <Building2 className="w-8 h-8 text-orange-500" />;
    }
    return <WifiOff className="w-8 h-8 text-red-500" />;
  };

  const getTitle = () => {
    if (isNamespaceError) {
      return "Empresa Não Encontrada";
    }
    return "Problema na Comunicação";
  };

  const getIconBgColor = () => {
    if (isNamespaceError) {
      return "bg-orange-100";
    }
    return "bg-red-100";
  };

  const getButtonColor = () => {
    if (isNamespaceError) {
      return "bg-orange-600 hover:bg-orange-700";
    }
    return "bg-blue-600 hover:bg-blue-700";
  };

  return (
    <div className="h-screen bg-gray-100 flex items-center justify-center p-6">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        {/* Ícone de erro */}
        <div className="mb-6">
          <div className={`w-16 h-16 mx-auto ${getIconBgColor()} rounded-full flex items-center justify-center`}>
            {getIcon()}
          </div>
        </div>

        {/* Título */}
        <h2 className="text-xl font-bold text-gray-800 mb-4">
          {getTitle()}
        </h2>

        {/* Mensagem */}
        <p className="text-gray-600 mb-6">
          {message}
        </p>

        {/* Detalhes específicos por tipo de erro */}
        {isNamespaceError ? (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
              <div className="text-left">
                <p className="text-sm font-medium text-orange-800">Problema de Configuração</p>
                <p className="text-sm text-orange-700 mt-1">
                  O namespace da empresa não foi encontrado no sistema. Isso pode acontecer quando:
                </p>
                <ul className="text-sm text-orange-700 mt-2 space-y-1">
                  <li>• A empresa não está cadastrada no sistema</li>
                  <li>• O namespace foi digitado incorretamente</li>
                  <li>• A empresa foi desativada ou removida</li>
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="text-left">
                <p className="text-sm font-medium text-red-800">Problema de Conectividade</p>
                <p className="text-sm text-red-700 mt-1">
                  Não foi possível estabelecer comunicação com o servidor. Verifique sua conexão.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Soluções específicas */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-gray-800 mb-2 flex items-center justify-center">
            <Settings className="w-4 h-4 mr-2" />
            {isNamespaceError ? "Como resolver:" : "Soluções recomendadas:"}
          </h3>
          <ul className="text-sm text-gray-600 space-y-1 text-left">
            {isNamespaceError ? (
              <>
                <li>• Volte à página inicial e verifique o namespace</li>
                <li>• Confirme o código da empresa com o administrador</li>
                <li>• Entre em contato com o suporte técnico</li>
                <li>• Solicite o cadastro da empresa no sistema</li>
              </>
            ) : (
              <>
                <li>• Verifique sua conexão com a internet</li>
                <li>• Confirme se o servidor está online</li>
                <li>• Tente recarregar a página (F5)</li>
                <li>• Entre em contato com o suporte se persistir</li>
              </>
            )}
          </ul>
        </div>

        {/* Botões de ação */}
        <div className="space-y-3">
          {isNamespaceError ? (
            // Para erro de namespace: apenas botão "Voltar à Página Inicial"
            onGoHome && (
              <button
                onClick={onGoHome}
                className={`w-full flex items-center justify-center space-x-2 px-6 py-3 text-white rounded-lg transition-all duration-200 font-medium ${getButtonColor()}`}
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Voltar à Página Inicial</span>
              </button>
            )
          ) : (
            // Para erro de comunicação: botão "Tentar Novamente"
            onRetry && (
              <button
                onClick={onRetry}
                disabled={isRetrying}
                className={`w-full flex items-center justify-center space-x-2 px-6 py-3 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium ${getButtonColor()}`}
              >
                {isRetrying ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Tentando conectar...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5" />
                    <span>Tentar Novamente</span>
                  </>
                )}
              </button>
            )
          )}
        </div>

        {/* Informação adicional */}
        <p className="text-sm text-gray-500 mt-4">
          {isNamespaceError
            ? "Sistema KDS - Monitor de Pedidos | Softcom Tecnologia"
            : "Verifique sua conexão e tente novamente"
          }
        </p>
      </div>
    </div>
  );
};

export default SimpleErrorScreen;